import api from "./api";

export interface DashboardStatsResponse {
  openTickets: number;
  totalTickets: number;
  totalMessages: number;
  totalClients: number;
  recentTickets: Array<{
    _id: string;
    protocolId?: string;
    title?: string;
    status?: string;
    priority?: string;
    clientName?: string;
    createdAt?: string;
  }>;
  volumeByDay: Array<{ name: string; tickets: number }>;
  topTicketTags: Array<{ tag: string; count: number }>;
  topClientsByTickets: Array<{ clientId: string; name: string; count: number }>;
  topAgentsByTickets: Array<{ agentId: unknown; name: string; count: number }>;
  ticketsByStatus: Array<{ status: string; label: string; count: number }>;
  ticketsByChannel: Array<{ channel: string; label: string; count: number }>;
  /** Nulo quando nenhum ticket da organização tem SLA de 1ª resposta configurada. */
  slaFirstResponse: { compliance: number; total: number; met: number } | null;
  /** Minutos até a 1ª resposta do agente, em média — nulo sem nenhum ticket já respondido. */
  avgFirstResponseMinutes: number | null;
}

export interface SidebarCountersResponse {
  openTickets: number;
  unreadConversations: number;
}

export interface TicketsByClientReportRow {
  /** "YYYY-MM" */
  month: string;
  clientId: string | null;
  clientName: string;
  /** Tickets criados nesse mês (independente de quando/se foram resolvidos). */
  ticketsOpened: number;
  /** Tickets resolvidos nesse mês (independente de quando foram criados). */
  ticketsClosed: number;
  /** Soma do tempo informado pelo agente ao resolver, em horas (já convertido, 2 casas). */
  totalHours: number;
  /** Quantos tickets do grupo foram resolvidos sem informar o tempo (não entram em totalHours). */
  ticketsWithoutHours: number;
}

export interface WhatsappMessagesReportRow {
  /** "YYYY-MM" */
  month: string;
  /** Mensagens enviadas pelos agentes via WhatsApp. */
  sent: number;
  /** Mensagens recebidas dos clientes via WhatsApp. */
  received: number;
}

export const dashboardService = {
  async getStats(): Promise<DashboardStatsResponse> {
    const response = await api.get<DashboardStatsResponse>("/app/dashboard/stats");
    return response.data;
  },

  /** Leve de propósito — usado pro badge do menu lateral, que fica pollando o tempo todo. */
  async getCounters(): Promise<SidebarCountersResponse> {
    const response = await api.get<SidebarCountersResponse>("/app/dashboard/counters");
    return response.data;
  },

  /** Só considera tickets já resolvidos, agrupados pelo mês em que foram resolvidos. */
  async getTicketsByClientReport(filters: {
    from?: Date;
    to?: Date;
    clientId?: string;
  }): Promise<TicketsByClientReportRow[]> {
    const params: Record<string, string> = {};
    if (filters.from) params.from = filters.from.toISOString();
    if (filters.to) params.to = filters.to.toISOString();
    if (filters.clientId) params.clientId = filters.clientId;
    const response = await api.get<TicketsByClientReportRow[]>("/app/dashboard/reports/tickets-by-client", {
      params,
    });
    return response.data;
  },

  /** Total da organização (não por cliente), agrupado por mês. */
  async getWhatsappMessagesReport(filters: { from?: Date; to?: Date }): Promise<WhatsappMessagesReportRow[]> {
    const params: Record<string, string> = {};
    if (filters.from) params.from = filters.from.toISOString();
    if (filters.to) params.to = filters.to.toISOString();
    const response = await api.get<WhatsappMessagesReportRow[]>("/app/dashboard/reports/whatsapp-messages", {
      params,
    });
    return response.data;
  },
};
